import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "ria_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    const stock = Number.isFinite(product.stock) ? product.stock : Infinity;
    const existing = items.find((i) => i.product_id === product.product_id);
    const current = existing ? existing.quantity : 0;
    const desired = current + qty;
    const next = Math.min(desired, stock);
    const capped = next < desired;
    if (capped) toast.warning(`Only ${stock} available.`);
    if (next <= current) return { added: false, capped, available: stock };
    setItems((prev) => {
      const ex = prev.find((i) => i.product_id === product.product_id);
      if (ex) {
        return prev.map((i) =>
          i.product_id === product.product_id ? { ...i, quantity: next, stock } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          name: product.name,
          price: product.price,
          packet_size_g: product.packet_size_g,
          image_url: product.image_url,
          stock,
          quantity: next,
        },
      ];
    });
    return { added: true, capped, available: stock };
  };

  const updateQty = (product_id, qty) => {
    if (qty <= 0) return removeItem(product_id);
    const item = items.find((i) => i.product_id === product_id);
    const stock = item && Number.isFinite(item.stock) ? item.stock : Infinity;
    if (qty > stock) toast.warning(`Only ${stock} available.`);
    const next = Math.min(qty, stock);
    setItems((prev) => prev.map((i) => (i.product_id === product_id ? { ...i, quantity: next } : i)));
  };

  const removeItem = (product_id) =>
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
